// Таблицата на „Взаимозаменени продукти": ниски редове на един ред. Широк
// екран — # · Дата · Потребител · Тип · Склад · От → На · Изписано · Покрито
// · Ед. цена · Нето · Бруто (датата и потребителят, типът и складът делят по
// клетка — всяко със свое заглавие и фуния); лаптоп/таблет — от тях остават
// количествата и брутото, а типът и складът са приглушено до двойката;
// телефон — карти. Заглавията сортират; редът „Общо за периода" е за всички
// показани. Редовете не се отварят — замяната няма собствен документ.
import type { ReactNode } from "react";
import { ArrowRightOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { fmtDateTime } from "../../components/dateTime";
import { formatMoney, formatUnitPrice } from "../../components/numbers";
import { formatQty } from "../../components/qty";
import { StatusPill } from "../../components/list/ListBits";
import { sectionList, type ColumnSections } from "../../components/filters/useColumnFilters";
import { ReportHead } from "./ReportListBits";
import {
  REPORT_HEAD_CLASS,
  REPORT_HEADER_ROW_CLASS,
  REPORT_ROW_CLASS,
  REPORT_TOTAL_ROW_CLASS,
} from "./reportListModel";
import {
  userOf,
  warehouseOf,
  type SubFilterKey,
  type SubSort,
  type SubSortKey,
  type SubstitutionRow,
} from "./substitutionsReportModel";

export type SubstitutionsDensity = "full" | "medium" | "cards";

// Литерални класове — Tailwind сканира само пълни низове
const GRID_CLASS = {
  full: "grid grid-cols-[32px_minmax(190px,1.1fr)_minmax(170px,1fr)_minmax(110px,1fr)_14px_minmax(110px,1fr)_92px_92px_76px_84px_92px] items-center gap-2.5 px-[18px]",
  medium: "grid grid-cols-[32px_minmax(180px,.8fr)_minmax(210px,1.4fr)_100px_100px_96px] items-center gap-2.5 px-[18px]",
} as const;
// Колоните преди сумите — етикетът „Общо за периода" ги заема
const TOTAL_LABEL_SPAN = { full: "col-span-9", medium: "col-span-5" } as const;

// Число + мерна единица: числото завършва вдясно, единицата започва на обща
// линия надолу (както в „Наличности").
const QtyCell = ({ value, unit, decimals }: { value: number; unit: string | null; decimals: number }) => (
  <span className="inline-flex items-baseline justify-end font-sheet tabular-nums">
    <span className="text-right font-bold text-ink">{formatQty(value, decimals)}</span>
    <span className="ml-1 min-w-[22px] text-left font-normal text-muted">{unit ?? ""}</span>
  </span>
);

const ProductName = ({ name, unit }: { name: string; unit: string | null }) => (
  <span className="min-w-0 truncate font-semibold text-ink">
    {name}
    {unit ? <span className="font-normal text-muted"> · {unit}</span> : null}
  </span>
);

type Props = {
  rows: SubstitutionRow[];
  density: SubstitutionsDensity;
  sourceLabel: (source: string) => string;
  sort: SubSort | null;
  onSort: (key: SubSortKey) => void;
  filters: ColumnSections<SubFilterKey>;
  totals: { net: number; gross: number };
  footer?: ReactNode;
};

export const SubstitutionsReportTable = ({
  rows,
  density,
  sourceLabel,
  sort,
  onSort,
  filters,
  totals,
  footer,
}: Props) => {
  const { t } = useTranslation();

  if (density === "cards") {
    return (
      <div>
        {rows.map((row) => (
          <div key={row.key} className="flex flex-col gap-[5px] border-t border-shade-1 px-3.5 py-3">
            <div className="flex min-w-0 items-center gap-2">
              <ProductName name={row.donorName} unit={null} />
              <ArrowRightOutlined className="shrink-0 text-[11px] text-faint" />
              <ProductName name={row.recipientName} unit={null} />
            </div>
            <div className="flex items-baseline justify-between gap-2.5">
              <span className="font-sheet text-[13px] text-ink-2">
                {formatQty(row.donorQty, row.donorDecimals)} {row.donorUnit ?? ""} →{" "}
                {formatQty(row.recipientQty, row.recipientDecimals)} {row.recipientUnit ?? ""}
              </span>
              <span className="font-sheet text-sm font-semibold text-ink">{formatMoney(row.sum)}</span>
            </div>
            <span className="truncate text-[12.5px] text-body">
              {fmtDateTime(row.date)} · {userOf(row)} · {sourceLabel(row.source)} · {warehouseOf(row)}
            </span>
          </div>
        ))}
        {footer}
        <div className="flex items-baseline justify-between gap-2.5 border-t border-shade-2 bg-fill px-3.5 py-3">
          <span className={REPORT_HEAD_CLASS}>{t("inventoryPage.periodTotal")}</span>
          <span className="font-sheet font-bold text-brand-deep-ink">{formatMoney(totals.gross)}</span>
        </div>
      </div>
    );
  }

  const grid = GRID_CLASS[density];
  const full = density === "full";
  const head = (
    label: string,
    options: { sortKey?: SubSortKey; align?: "right"; filters?: ReturnType<typeof sectionList> } = {},
  ) => <ReportHead label={label} sort={sort} onSort={onSort} {...options} />;
  const dot = <span className="text-faint">·</span>;

  return (
    <div className="overflow-x-auto">
      <div className={full ? "min-w-[1180px]" : "min-w-[790px]"}>
        <div className={`${grid} ${REPORT_HEADER_ROW_CLASS}`}>
          {head("#")}
          <span className="flex min-w-0 items-center gap-x-1.5">
            {head(t("inventoryPage.colDate"), { sortKey: "date" })}
            {dot}
            {head(t("inventoryPage.colUser"), { sortKey: "user", filters: sectionList(filters.users) })}
          </span>
          {full ? (
            <>
              <span className="flex min-w-0 items-center gap-x-1.5">
                {head(t("inventoryPage.colType"), { filters: sectionList(filters.types) })}
                {dot}
                {head(t("inventoryPage.colWarehouse"), { filters: sectionList(filters.warehouses) })}
              </span>
              {head(t("inventoryPage.subColFrom"), { sortKey: "donor", filters: sectionList(filters.donors) })}
              <span />
              {head(t("inventoryPage.subColTo"), { sortKey: "recipient", filters: sectionList(filters.recipients) })}
            </>
          ) : (
            head(`${t("inventoryPage.subColFrom")} → ${t("inventoryPage.subColTo")}`, {
              sortKey: "donor",
              filters: sectionList(filters.donors, filters.recipients, filters.types, filters.warehouses),
            })
          )}
          {head(t("inventoryPage.subWrittenOff"), { sortKey: "donorQty", align: "right" })}
          {head(t("inventoryPage.subCovered"), { sortKey: "recipientQty", align: "right" })}
          {full && (
            <>
              {head(t("inventoryPage.colUnitPrice"), { sortKey: "unitPrice", align: "right" })}
              {head(t("inventoryPage.colNet"), { sortKey: "net", align: "right" })}
            </>
          )}
          {head(t("inventoryPage.colGross"), { sortKey: "gross", align: "right" })}
        </div>
        {rows.map((row, index) => (
          <div key={row.key} className={`${grid} ${REPORT_ROW_CLASS} hover:bg-fill`}>
            <span className="font-sheet text-faint">{index + 1}</span>
            <span className="flex min-w-0 items-baseline gap-2">
              <span className="shrink-0 whitespace-nowrap font-sheet text-ink-2">{fmtDateTime(row.date)}</span>
              <span className="min-w-0 truncate font-medium text-ink">{userOf(row)}</span>
            </span>
            {full ? (
              <>
                <span className="flex min-w-0 items-center gap-2">
                  <StatusPill tone="brand">{sourceLabel(row.source)}</StatusPill>
                  <span className="min-w-0 truncate text-body">{warehouseOf(row)}</span>
                </span>
                <ProductName name={row.donorName} unit={row.donorUnit} />
                <ArrowRightOutlined className="text-xs text-faint" />
                <ProductName name={row.recipientName} unit={row.recipientUnit} />
              </>
            ) : (
              <span className="flex min-w-0 items-center gap-2">
                <ProductName name={row.donorName} unit={null} />
                <ArrowRightOutlined className="shrink-0 text-[11px] text-faint" />
                <ProductName name={row.recipientName} unit={null} />
                <span className="min-w-0 shrink-[2] truncate text-muted">
                  · {sourceLabel(row.source)} · {warehouseOf(row)}
                </span>
              </span>
            )}
            <span className="text-right">
              <QtyCell value={row.donorQty} unit={row.donorUnit} decimals={row.donorDecimals} />
            </span>
            <span className="text-right">
              <QtyCell value={row.recipientQty} unit={row.recipientUnit} decimals={row.recipientDecimals} />
            </span>
            {full && (
              <>
                <span className="text-right font-sheet tabular-nums text-ink-2">{formatUnitPrice(row.unitPrice)}</span>
                <span className="text-right font-sheet tabular-nums text-ink-2">{formatMoney(row.sumNoVat)}</span>
              </>
            )}
            <span className="text-right font-sheet font-bold tabular-nums text-ink">{formatMoney(row.sum)}</span>
          </div>
        ))}
        {footer}
        {rows.length > 0 && (
          <div className={`${grid} ${REPORT_TOTAL_ROW_CLASS}`}>
            <span className={`${REPORT_HEAD_CLASS} ${TOTAL_LABEL_SPAN[density]}`}>{t("inventoryPage.periodTotal")}</span>
            {full && <span className="text-right font-sheet font-semibold text-ink">{formatMoney(totals.net)}</span>}
            <span className="text-right font-sheet font-bold text-brand-deep-ink">{formatMoney(totals.gross)}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubstitutionsReportTable;
